import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Chip,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import debounce from 'lodash.debounce'
import { startSxpProxy } from '../../utils/api'
import { CHAT_PROJECT_ID } from '../../utils/constants'
import { allocateApi } from '../bedManagement/modals'
import { makeName } from '../lms/utils'
import { toast } from 'react-toastify'
import KeycloakService from '../../utils/keycloakService'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import {
  ADD_REFERRAL,
  BTN_BACK,
  BTN_CANCEL,
  BTN_SUBMIT,
  BTN_UPDATE,
  EDIT_REFERRAL,
  REFERRAL_DASHBOARD_ROUTE,
  REFERRAL_FORM_DEPARTMENT_LABEL,
  REFERRAL_FORM_FETCH_DEPARTMENTS_ERROR,
  REFERRAL_FORM_IS_SALES_PERSON_LABEL,
  REFERRAL_FORM_PATIENT_NAME_LABEL,
  REFERRAL_FORM_PATIENT_UHID_LABEL,
  REFERRAL_FORM_REASON_LABEL,
  REFERRAL_FORM_REFERRED_DOCTOR_LABEL,
  REFERRAL_FORM_REFERRED_HOSPITAL_LABEL,
  REFERRAL_FORM_REFERRAL_DATE_LABEL,
  REFERRAL_FORM_REFERRAL_NAME_LABEL,
  REFERRAL_FORM_SERVICE_LABEL,
  referralValidationSchema,
  SERVICE_OPTIONS,
  TOAST_PATIENT_FETCH_FAIL,
  TOAST_PATIENT_NOT_FOUND,
  TOAST_REFERRAL_SUBMIT_FAIL,
  TOAST_REFERRAL_SUBMIT_SUCCESS,
  TOAST_REFERRAL_UPDATE_FAIL,
  TOAST_REFERRAL_UPDATE_SUCCESS,
  UNKNOWN_USER,
} from './constants'
import {
  createReferral,
  fetchDepartments,
  fetchDoctorsAsync,
  fetchHospitalList,
  updateReferral,
} from '../../services/referralService'
import { useAppSelector } from '../../app/hooks'
import {
  selectSelectedLocationId,
  selectLocations,
} from '../location/locationSlice'

interface ReferralFormProps {
  /** When provided, form renders inside a Dialog controlled by this flag */
  open?: boolean
  /** Called when the dialog should close (dialog mode only) */
  onClose?: () => void
  /** Pre-fill patient fields when opening from Patient Table */
  patient?: { uhid: string; name: string; fhirId: string }
  /** Existing referral record for editing */
  referral?: any
}

interface ReferralFormData {
  patientId?: string
  patientUhid: string
  patientName: string
  service: string[]
  department: string
  referringDoctor: string
  referredDoctor: string
  referredHospital: string
  referralDate: string
  reason: string
  isSalesPerson: boolean
}

const styles = () => ({
  referralField: {
    '& .MuiInputBase-input': { fontSize: '14px', padding: '8px 8px' },
    '& .MuiInputLabel-root': { fontSize: '14px' },
    '& .MuiSelect-select': { fontSize: '14px', padding: '8px 8px' },
  },
  serviceAutocomplete: {
    '& .MuiInputBase-root': {
      overflow: 'visible',
    },
  },
  selfReferralCheckbox: {
    padding: 0,
  },
  selfReferralLabel: {
    margin: 0,
    flexShrink: 0,
    gap: '4px',
  },
  pageTitle: {
    fontWeight: 600,
    fontSize: '16px',
    color: '#090968',
  },
  chipModel: {
    '& .MuiChip-label': {
      overflow: 'visible',
    },
  },
})

const ReferralFormContent = ({
  onCancel,
  patient,
  referral,
}: {
  onCancel?: () => void
  patient?: { uhid: string; name: string; fhirId: string }
  referral?: any
}) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [departments, setDepartments] = useState<any>([])
  const [hospitals, setHospitals] = useState<any>([])
  const [doctors, setDoctors] = useState<any>([])
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [isSelfReferral, setIsSelfReferral] = useState(
    referral?.is_sales_person ?? false
  )
  const locationId = useAppSelector(selectSelectedLocationId)
  const locations = useAppSelector(selectLocations)
  const selectedLocation = locations?.find(
    (loc: any) => loc?.resource?.id === locationId
  )?.resource?.name

  const getAllDepartments = useCallback(async () => {
    try {
      const data = await fetchDepartments()
      setDepartments(data)
    } catch (error) {
      console.error(REFERRAL_FORM_FETCH_DEPARTMENTS_ERROR, error)
    }
  }, [])

  useEffect(() => {
    getAllDepartments()
  }, [getAllDepartments])

  const getAllHospitals = useCallback(async () => {
    const result = await fetchHospitalList()
    setHospitals(result?.data ?? [])
  }, [])
  const getDoctors = useCallback(async () => {
    const result = await fetchDoctorsAsync()
    setDoctors(result ?? [])
  }, [])
  useEffect(() => {
    getDoctors()
  }, [getDoctors])
  useEffect(() => {
    getAllHospitals()
  }, [getAllHospitals])

  const getReferralDateTime = (referralDate: string) => {
    const now = new Date()
    const [year, month, day] = (referralDate?.split('-') ?? []).map(Number)
    return new Date(
      year,
      month - 1,
      day,
      now.getHours(),
      now.getMinutes(),
      now.getSeconds()
    ).toISOString()
  }

  const handleCreate = async (
    values: ReferralFormData,
    referralDateTime: string
  ) => {
    const object = {
      patient_fhir_id: values?.patientId ?? null,
      patient_uhid: values?.patientUhid,
      patient_name: values?.patientName,
      department: values?.department,
      referred_to: values?.referringDoctor ?? null,
      referred_from: values?.referredDoctor ?? null,
      hospital: values?.referredHospital,
      referral_date: referralDateTime,
      service: values?.service?.join(', '),
      notes: values?.reason,
      created_by: KeycloakService?.getUsername() || UNKNOWN_USER,
      source: selectedLocation,
      is_sales_person: values?.isSalesPerson,
    }
    await createReferral(object)
    toast.success(TOAST_REFERRAL_SUBMIT_SUCCESS)
    navigate(REFERRAL_DASHBOARD_ROUTE)
    if (onCancel) onCancel()
  }

  const handleUpdate = async (
    values: ReferralFormData,
    referralDateTime: string
  ) => {
    const object = {
      id: referral?.id,
      patient_fhir_id: values?.patientId ?? null,
      patient_uhid: values?.patientUhid,
      patient_name: values?.patientName,
      department: values?.department,
      is_sales_person: values?.isSalesPerson ?? false,
      referred_to: values?.referringDoctor ?? null,
      referred_from: values?.referredDoctor ?? null,
      hospital: values?.referredHospital,
      referral_date: referralDateTime,
      service: values?.service?.join(', '),
      notes: values?.reason,
      updated_by: KeycloakService?.getUsername() || UNKNOWN_USER,
      updated_on: new Date().toISOString(),
      source: selectedLocation,
    }
    await updateReferral(referral?.id, object)
    toast.success(TOAST_REFERRAL_UPDATE_SUCCESS)
    onCancel && onCancel()
  }

  const handleSubmit = async (values: ReferralFormData) => {
    setIsSubmitDisabled(true)

    const referralDateTime = getReferralDateTime(values?.referralDate)
    try {
      if (referral?.id) {
        await handleUpdate(values, referralDateTime)
      } else {
        await handleCreate(values, referralDateTime)
      }
    } catch {
      toast.error(
        referral?.id ? TOAST_REFERRAL_UPDATE_FAIL : TOAST_REFERRAL_SUBMIT_FAIL
      )
    } finally {
      setIsSubmitDisabled(false)
    }
  }

  const formik = useFormik<ReferralFormData>({
    initialValues: {
      patientId: referral?.patient_fhir_id ?? patient?.fhirId ?? undefined,
      patientUhid: referral?.patient_uhid ?? patient?.uhid ?? '',
      patientName: referral?.patient_name ?? patient?.name ?? '',
      service: referral?.service
        ? referral?.service?.split(', ')?.filter(Boolean)
        : [],
      department: referral?.department ?? '',
      referringDoctor: referral?.referred_to ?? '',
      referredDoctor: referral?.referred_from ?? '',
      referredHospital: referral?.hospital ?? '',
      referralDate:
        referral?.referral_date?.split('T')?.[0] ??
        new Date().toISOString().split('T')?.[0],
      reason: referral?.notes ?? '',
      isSalesPerson: referral?.is_sales_person ?? false,
    },
    enableReinitialize: true,
    validationSchema: referralValidationSchema,
    onSubmit: handleSubmit,
  })

  const fetchPatientByUhid = useCallback(
    (uhid: string) => {
      if (!uhid) return
      startSxpProxy(
        CHAT_PROJECT_ID,
        allocateApi.getPatientDetailsByIdentifier,
        {
          patientIdentifier: uhid,
        }
      )
        .then((data) => {
          if (!data?.data?.entry?.length) {
            formik.setValues((prev) => ({
              ...prev,
              patientName: '',
              patientId: '',
            }))
            toast.error(TOAST_PATIENT_NOT_FOUND, { autoClose: 3000 })
            return
          }
          const name = makeName(data?.data?.entry?.[0]?.resource?.name)
          formik.setValues((prev) => ({
            ...prev,
            patientName: name ?? '',
            patientId: data?.data?.entry?.[0]?.resource?.id,
          }))
        })
        .catch(() => {
          formik.setValues((prev) => ({
            ...prev,
            patientName: '',
            patientId: '',
          }))
          toast.error(TOAST_PATIENT_FETCH_FAIL, { autoClose: 3000 })
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const debouncedFetch = useMemo(
    () => debounce(fetchPatientByUhid, 500),
    [fetchPatientByUhid]
  )

  useEffect(() => {
    return () => {
      debouncedFetch.cancel()
    }
  }, [debouncedFetch])

  const handleUhidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    formik.setValues((prev) => ({
      ...prev,
      patientUhid: value,
      patientName: '',
      patientId: '',
    }))
    formik.setFieldTouched('patientUhid', true, false)
    if (!value.trim()) {
      debouncedFetch.cancel()
      return
    }
    debouncedFetch(value)
  }

  const handleSelfReferralToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setIsSelfReferral(checked)
    formik.setFieldValue(
      'referringDoctor',
      checked ? KeycloakService?.getUsername() ?? '' : ''
    )
    formik.setFieldValue('isSalesPerson', checked)
  }
  const doctorsNames = useMemo(() => {
    return doctors?.map((d: any) => d?.firstName + ' ' + d?.lastName) ?? []
  }, [doctors])
  useEffect(() => {
    setIsSelfReferral(referral?.is_sales_person ?? false)
  }, [referral])
  return (
    <Box display='flex' flexDirection='column' gap={1}>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            label={REFERRAL_FORM_PATIENT_UHID_LABEL}
            name='patientUhid'
            value={formik.values?.patientUhid}
            onChange={patient || referral ? undefined : handleUhidChange}
            onBlur={patient || referral ? undefined : formik.handleBlur}
            inputProps={{ readOnly: !!(patient || referral) }}
            size='small'
            fullWidth
            className={classes?.referralField}
            error={formik.touched?.patientUhid && !!formik.errors?.patientUhid}
            helperText={
              formik.touched?.patientUhid ? formik.errors?.patientUhid : ''
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            inputProps={{ readOnly: true }}
            label={REFERRAL_FORM_PATIENT_NAME_LABEL}
            name='patientName'
            value={formik.values?.patientName}
            size='small'
            fullWidth
            className={classes?.referralField}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            freeSolo
            className={classes?.serviceAutocomplete}
            options={SERVICE_OPTIONS}
            value={formik.values?.service}
            onChange={(_, newValue) => {
              formik.setFieldValue('service', newValue)
              if (newValue.length)
                formik.setFieldTouched('service', true, false)
            }}
            renderTags={(value, getTagProps) =>
              value?.map((option, index) => (
                <div key={`${option?.trim() || 'service'}-${index}`}>
                  <Chip
                    label={option}
                    size='small'
                    {...getTagProps({ index })}
                    className={classes?.chipModel}
                  />
                </div>
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={REFERRAL_FORM_SERVICE_LABEL}
                size='small'
                className={classes?.referralField}
                error={formik.touched?.service && !!formik.errors?.service}
                helperText={
                  formik.touched?.service && formik.errors?.service
                    ? (formik.errors?.service as string)
                    : ''
                }
                onBlur={formik.handleBlur}
                name='service'
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={departments?.map((d: any) => d?.display ?? '')}
            value={formik.values?.department}
            onChange={(_, newValue) => {
              formik.setFieldValue('department', newValue ?? '')
            }}
            onInputChange={(_, newInputValue) => {
              formik.setFieldValue('department', newInputValue)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={REFERRAL_FORM_DEPARTMENT_LABEL}
                size='small'
                className={classes?.referralField}
                error={
                  formik.touched?.department && !!formik.errors?.department
                }
                helperText={
                  formik.touched?.department ? formik.errors?.department : ''
                }
                onBlur={formik.handleBlur}
                name='department'
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box display='flex' alignItems='center' gap={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSelfReferral}
                  onChange={handleSelfReferralToggle}
                  size='small'
                  className={classes?.selfReferralCheckbox}
                />
              }
              label={REFERRAL_FORM_IS_SALES_PERSON_LABEL}
              className={classes?.selfReferralLabel}
            />
            {isSelfReferral ? (
              <TextField
                label={REFERRAL_FORM_REFERRAL_NAME_LABEL}
                value={formik.values?.referringDoctor}
                inputProps={{ readOnly: true }}
                size='small'
                fullWidth
                className={classes?.referralField}
              />
            ) : (
              <Autocomplete
                freeSolo
                fullWidth
                options={doctorsNames}
                value={formik.values?.referringDoctor}
                onChange={(_, newValue) => {
                  formik.setFieldValue('referringDoctor', newValue ?? '')
                }}
                onInputChange={(_, newInputValue) => {
                  formik.setFieldValue('referringDoctor', newInputValue)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={REFERRAL_FORM_REFERRAL_NAME_LABEL}
                    size='small'
                    className={classes?.referralField}
                  />
                )}
              />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={hospitals?.map((h: any) => h?.name ?? '')}
            value={formik.values?.referredHospital}
            onChange={(_, newValue) => {
              formik.setFieldValue('referredHospital', newValue ?? '')
            }}
            onInputChange={(_, newInputValue) => {
              formik.setFieldValue('referredHospital', newInputValue)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={REFERRAL_FORM_REFERRED_HOSPITAL_LABEL}
                size='small'
                className={classes?.referralField}
                error={
                  formik.touched?.referredHospital &&
                  !!formik.errors?.referredHospital
                }
                helperText={
                  formik.touched?.referredHospital
                    ? formik.errors?.referredHospital
                    : ''
                }
                onBlur={formik.handleBlur}
                name='referredHospital'
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={[]}
            value={formik.values?.referredDoctor}
            onChange={(_, newValue) => {
              formik.setFieldValue('referredDoctor', newValue ?? '')
            }}
            onInputChange={(_, newInputValue) => {
              formik.setFieldValue('referredDoctor', newInputValue)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={REFERRAL_FORM_REFERRED_DOCTOR_LABEL}
                size='small'
                className={classes?.referralField}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={REFERRAL_FORM_REFERRAL_DATE_LABEL}
            name='referralDate'
            type='date'
            value={formik.values?.referralDate}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            size='small'
            fullWidth
            InputLabelProps={{ shrink: true }}
            className={classes?.referralField}
            error={
              formik.touched?.referralDate && !!formik.errors?.referralDate
            }
            helperText={
              formik.touched?.referralDate ? formik.errors?.referralDate : ''
            }
          />
        </Grid>
        <Grid item xs={8}>
          <TextField
            label={REFERRAL_FORM_REASON_LABEL}
            name='reason'
            value={formik.values?.reason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            size='small'
            fullWidth
            multiline
            rows={2}
            className={classes?.referralField}
            error={formik.touched?.reason && !!formik.errors?.reason}
            helperText={formik.touched?.reason ? formik.errors?.reason : ''}
          />
        </Grid>
      </Grid>
      <Box display='flex' justifyContent='flex-end' gap={1}>
        <Button variant='outlined' size='small' onClick={onCancel}>
          {BTN_CANCEL}
        </Button>
        <Button
          variant='contained'
          size='small'
          color='primary'
          onClick={() => formik.handleSubmit()}
          disabled={isSubmitDisabled || (referral ? !formik.dirty : false)}
        >
          {referral ? BTN_UPDATE : BTN_SUBMIT}
        </Button>
      </Box>
    </Box>
  )
}

const ReferralForm = ({
  open,
  onClose,
  patient,
  referral,
}: ReferralFormProps) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  // ── Dialog mode ──────────────────────────────────────────────────────────
  if (open !== undefined) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography fontWeight={600}>
              {referral ? EDIT_REFERRAL : ADD_REFERRAL}
            </Typography>
            <IconButton size='small' onClick={onClose}>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <ReferralFormContent
            onCancel={onClose}
            patient={patient}
            referral={referral}
          />
        </DialogContent>
      </Dialog>
    )
  }

  // ── Separate page mode (default) ─────────────────────────────────────────
  return (
    <Box
      height='100%'
      width='100%'
      padding={2}
      display='flex'
      flexDirection='column'
      gap={2}
    >
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Typography className={classes?.pageTitle}>{ADD_REFERRAL}</Typography>
        <Button
          variant='outlined'
          size='small'
          onClick={() => navigate(REFERRAL_DASHBOARD_ROUTE)}
        >
          {BTN_BACK}
        </Button>
      </Box>
      <ReferralFormContent
        onCancel={() => navigate(REFERRAL_DASHBOARD_ROUTE)}
      />
    </Box>
  )
}

export default ReferralForm
